import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Bell, Key, ShieldCheck, X, ArrowRight, Bot } from 'lucide-react';
import { cn } from '../lib/utils';
import { Language } from '../types';
import { StoreLoginModal } from './StoreLoginModal';

interface StorePushNotificationBannerProps {
  lang: Language;
  theme?: 'light' | 'dark';
  storeName?: string;
  onLoginSaved?: () => void;
}

export const StorePushNotificationBanner: React.FC<StorePushNotificationBannerProps> = ({
  lang,
  theme = 'dark',
  storeName = 'Silpo / ATB',
  onLoginSaved
}) => {
  const isUa = lang === 'ua';
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // Check if store login exists in localStorage or was recently dismissed
    const hasUser = !!localStorage.getItem('nutri_agent_user');
    const googleStores = localStorage.getItem('nutri_agent_google_stores');
    const hasGoogle = googleStores && googleStores !== '{}';
    const isDismissed = localStorage.getItem('nutri_agent_banner_dismissed') === 'true';

    if (!hasUser && !hasGoogle && !isDismissed) {
      // Show push notification after a polite delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('nutri_agent_banner_dismissed', 'true');
  };

  const handleOpenLogin = () => {
    setIsModalOpen(true);
  };

  const handleSuccess = () => {
    setIsVisible(false);
    if (onLoginSaved) onLoginSaved();
  };

  if (!isVisible && !isModalOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[110] w-[92%] max-w-lg"
          >
            <div className={cn(
              "p-4 rounded-2xl border shadow-2xl flex items-start gap-3 backdrop-blur-xl relative overflow-hidden",
              theme === 'light'
                ? "bg-white/95 border-amber-300 text-zinc-900 shadow-amber-500/10"
                : "bg-zinc-950/95 border-emerald-500/40 text-white shadow-[0_10px_30px_rgba(16,185,129,0.25)]"
            )}>
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 shrink-0 mt-0.5">
                <Bell className="w-5 h-5 animate-bounce" />
              </div>

              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    {isUa ? 'PUSH-СПОВІЩЕННЯ NUTRI-AGENT' : 'NUTRI-AGENT PUSH ALERT'}
                  </span>
                  <span className="flex items-center gap-1 text-[9px] font-mono text-emerald-400">
                    <ShieldCheck className="w-3 h-3" />
                    {isUa ? 'Захищено' : 'Secure'}
                  </span>
                </div>

                <p className="text-xs font-bold leading-snug text-zinc-800 dark:text-zinc-100">
                  {isUa
                    ? `Відсутні облікові дані або мобільний додаток магазину (${storeName}).`
                    : `No grocery store login info or mobile app credentials found (${storeName}).`}
                </p>

                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-tight">
                  {isUa
                    ? 'Увійдіть через Google або введіть пароль, щоб ШІ міг автономно збирати ваш кошик. Дані будуть надійно збережені.'
                    : 'Please sign in with Google or enter login data so Nutri-Agent can place orders on your behalf.'}
                </p>

                <div className="pt-2 flex items-center gap-2">
                  <button
                    onClick={handleOpenLogin}
                    className="px-3.5 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-extrabold text-xs transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
                  >
                    <Key className="w-3.5 h-3.5" />
                    <span>{isUa ? 'Увійти / Google Auth' : 'Connect Account / Google'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDismiss}
                    className="px-2.5 py-1.5 rounded-xl text-xs font-bold text-zinc-400 hover:text-zinc-200"
                  >
                    {isUa ? 'Пізніше' : 'Later'}
                  </button>
                </div>
              </div>

              <button
                onClick={handleDismiss}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-200 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <StoreLoginModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        targetStore={storeName}
        lang={lang}
        theme={theme}
        onSuccess={handleSuccess}
      />
    </>
  );
};
